/**
 * Pure schedule-readiness checks for Game Pass monthly challenges.
 *
 * Before a draft monthly campaign can be scheduled it must be structurally
 * complete:
 *   1. It holds at least one challenge.
 *   2. Every challenge is fully authored — a valid name, description,
 *      type-specific fields and at least one complete tier (threshold, reward
 *      amount, reward currency and XP reward), i.e. no empty fields.
 *
 * When a challenge is incomplete we surface the first offending challenge by
 * name so the admin knows exactly where to finish authoring — rather than
 * listing every incomplete challenge at once.
 *
 * Mirrors `seasonScheduleReadiness` and is kept framework-free (no React) so it
 * can be unit-tested in isolation and reused by any surface that schedules a
 * monthly campaign.
 */

import { AppStrings } from "../../utils/constants/AppStrings";
import {
  isMonthlyChallengeValid,
  type MonthlyChallengeDraft,
} from "../../components/GamePass/Monthly/monthlyChallengeData";

/** The kind of readiness problem found, used to build the user-facing copy. */
export type MonthlyScheduleReadinessIssue =
  | { type: "NO_CHALLENGES" }
  | { type: "CHALLENGE_INCOMPLETE"; challengeName: string };

/**
 * Returns the first readiness issue that blocks scheduling, or `null` when the
 * campaign is ready. The check is short-circuited at the first failure so a
 * single incomplete challenge is reported rather than every one.
 */
export const findMonthlyScheduleReadinessIssue = (
  challenges: ReadonlyArray<MonthlyChallengeDraft>,
): MonthlyScheduleReadinessIssue | null => {
  if (challenges.length === 0) {
    return { type: "NO_CHALLENGES" };
  }

  const incompleteChallenge = challenges.find(
    (challenge) => !isMonthlyChallengeValid(challenge),
  );
  if (incompleteChallenge) {
    return {
      type: "CHALLENGE_INCOMPLETE",
      challengeName: incompleteChallenge.name?.trim() ?? "",
    };
  }

  return null;
};

/**
 * Build the user-facing error message for a detected readiness issue. Names the
 * incomplete challenge so the admin knows where to finish authoring; falls back
 * to a generic message when the challenge has no resolvable name.
 */
export const buildMonthlyScheduleReadinessMessage = (
  issue: MonthlyScheduleReadinessIssue,
): string => {
  if (issue.type === "NO_CHALLENGES") {
    return AppStrings.GAME_PASS_MONTHLY_SCHEDULE_NO_CHALLENGES;
  }

  const name = issue.challengeName.trim();
  if (!name) {
    return AppStrings.GAME_PASS_MONTHLY_SCHEDULE_CHALLENGE_INCOMPLETE_GENERIC;
  }
  return AppStrings.GAME_PASS_MONTHLY_SCHEDULE_CHALLENGE_INCOMPLETE.replace(
    "{challengeName}",
    name,
  );
};
